import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button, SelectField, TextField } from '@/DesignSystem/Component';
import { useShadUser } from '@/Module/Auth/Hook';
import { FORM_GLOBAL_VALIDATION_ERROR } from '@/Module/Core/Constant';
import { yup } from '@/Module/Core/Helper';
import type { ILabelValue } from '@/Module/Core/Model/DataModel';
import { CookieService } from '@/Module/Core/Service';
import { useFindCategory, useFindField } from '@/Module/Grade/Hook';
import { IconMobile, IconUserSquare } from '@/Module/Icon';
import { useAppSelector } from '@/Module/Store/Hook';
export interface ICompleteProfileForm {
  mobile: string;
  firstName: string;
  lastName: string;
  grad: ILabelValue;
  field?: ILabelValue;
  birthDate?: any;
  province?: ILabelValue;
  city?: ILabelValue;
}
interface ICompleteProfileFormProps {
  isRegister?: boolean;
  onSuccess: (payload: ICompleteProfileForm) => void;
  editProfile?: boolean;
}
const NEED_FIELD_GRADES = ['هفتم', 'هشتم', 'نهم', 'ششم', 'پنجم', 'چهارم', 'سوم', 'دوم', 'اول'];
const CompleteProfileForm: FC<ICompleteProfileFormProps> = ({
  isRegister = false,
  onSuccess,
  editProfile
}) => {
  const router = useRouter();
  const isShadUser = useShadUser();
  const fieldData = useFindField();
  const optionsGrad = useFindCategory();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const person = useAppSelector(state => state.auth.person);
  const preparedFieldList: ILabelValue[] = useMemo(() => {
    return fieldData?.map(item => ({
      value: item.id,
      label: item.categoryName
    }));
  }, [fieldData]);
  const preparedGradeList: ILabelValue[] = useMemo(() => {
    return optionsGrad?.map(item => ({
      value: item.id,
      label: item.categoryName
    }));
  }, [optionsGrad]);
  const rubikaUser = CookieService.getRubikaUser();
  const schema = useMemo(() => {
    return yup.object({
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      grad: yup.object().required('انتخاب پایه اجباری است!'),
      field: yup.mixed().nullable().test({
        message: 'لطفا رشته را انتخاب نمایید.',
        name: 'fieldValidation',
        test: (value, context) => {
          return NEED_FIELD_GRADES.includes(context.parent.grad?.label) || !!value;
        }
      }),
      province: yup.object().nullable().optional(),
      city: yup.mixed().nullable().test({
        message: 'لطفا شهر را انتخاب نمایید.',
        name: 'cityValidation',
        test: (value, context) => {
          return !context.parent.province?.label || !!value;
        }
      }).nullable()
    });
  }, []);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch
  } = useForm<ICompleteProfileForm>({
    resolver: yupResolver(schema as any)
  });
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const grade = watch('grad');
  const fieldIsDisabled = useMemo(() => {
    return !grade?.label || NEED_FIELD_GRADES.includes(grade.label as string);
  }, [grade]);
  const preparedFieldListCustom = useMemo(() => {
    if (grade?.label === 'دوازدهم') {
      return preparedFieldList;
    }
    return preparedFieldList.slice(0, 3);
  }, [grade, fieldData]);
  const onSubmit = handleSubmit(data => {
    onSuccess({
      ...data,
      field: data.field,
      grad: data.grad,
      province: data.province,
      city: data.city,
      birthDate: data.birthDate || null
    });
  }, () => {
    enqueueSnackbar({
      variant: 'error',
      message: FORM_GLOBAL_VALIDATION_ERROR
    });
  });
  useEffect(() => {
    if (!person) return;
    const foundGrade = preparedGradeList.find(item => item.value === person.gradeId);
    const foundField = preparedFieldList.find(item => item.value === person.fieldId);
    reset({
      mobile: person?.phoneNumber ?? '',
      firstName: person?.name ?? '',
      lastName: person?.family ?? '',
      grad: foundGrade || undefined,
      field: !NEED_FIELD_GRADES.includes(foundGrade?.label as string) ? foundField : undefined,
      birthDate: person.birthDate?.toString() || undefined
    });
  }, [person]);
  const foundGrade = useMemo(() => {
    return preparedGradeList.find(item => item.value === person?.gradeId);
  }, [preparedGradeList, person]);
  const foundField = preparedFieldList.find(item => item.value === person?.fieldId);
  useEffect(() => {
    if (!foundGrade) return;
    if (NEED_FIELD_GRADES.includes(foundGrade.label as string)) {
      setValue('field', undefined);
    }
    setValue('grad', foundGrade || undefined);
  }, [foundGrade, person]);
  useEffect(() => {
    if (!foundField) return;
    setValue('field', foundField || undefined);
  }, [foundField, person]);
  useEffect(() => {
    if (fieldIsDisabled) setValue('field', undefined);
  }, [fieldIsDisabled]);
  useEffect(() => {
    if (!isRegister) return;
    if (String(router.query?.mobile).length > 10) setValue('mobile', String(router.query?.mobile));
    if (rubikaUser) {
      if (rubikaUser?.name) setValue('firstName', rubikaUser.name);
      if (rubikaUser?.family) setValue('lastName', rubikaUser.family);
      CookieService.setRubikaUser('');
    }
  }, [isRegister, rubikaUser]);
  useEffect(() => {
    if (!(editProfile || !isShadUser || !firstName || !lastName || !grade || grade && grade.value > 13)) onSubmit();
  }, [isShadUser, firstName, lastName, grade]);
  return <form onSubmit={onSubmit} data-sentry-component="CompleteProfileForm" data-sentry-source-file="CompleteProfileForm.tsx">
      <TextField control={control} name="mobile" label={isShadUser ? 'شناسه یکتای شاد' : 'شماره موبایل'} prefix={<IconMobile />} className="mb-4" autoComplete="off" disabled data-sentry-element="TextField" data-sentry-source-file="CompleteProfileForm.tsx" />

      <TextField control={control} name="firstName" label="نام" prefix={<IconUserSquare />} className="mb-4" autoComplete="off" data-sentry-element="TextField" data-sentry-source-file="CompleteProfileForm.tsx" />

      <TextField control={control} name="lastName" label="نام خانوادگی" prefix={<IconUserSquare />} className="mb-4" autoComplete="off" data-sentry-element="TextField" data-sentry-source-file="CompleteProfileForm.tsx" />
      <SelectField label="انتخاب پایه" control={control} isLoading={false} isClearable isRtl name="grad" options={preparedGradeList} placeholder="" menuPlacement="top" data-sentry-element="SelectField" data-sentry-source-file="CompleteProfileForm.tsx" />
      {!fieldIsDisabled && <SelectField label="انتخاب رشته" control={control} isLoading={false} isClearable isRtl name="field" options={preparedFieldListCustom} placeholder="" menuPlacement="top" />}
      <Button id="profile" bold className="relative mt-4 w-full" variant="shadow" color="primary" radius="circle" size="large" type="submit" data-sentry-element="Button" data-sentry-source-file="CompleteProfileForm.tsx">
        {editProfile ? 'تایید' : 'تایید و مرحله بعدی'}
      </Button>
    </form>;
};
export default CompleteProfileForm;